import cloneDeep from 'lodash-es/cloneDeep';

import { InsightFilter, LogsAnalytics } from '@/shared/types/analytics';
import type { LogsFilterType } from '@/shared/types/analytics';

/**
 * Default values for boolean filters.
 * Applied when filters are opened for the first time.
 * Using null to avoid immediate filtering until user makes explicit selection.
 */
const BOOLEAN_FILTER_DEFAULTS: Record<string, boolean | null> = {
  hasCollection: null, // No default filtering - show all
  hasRecording: null, // No default filtering - show all
  hasVoicemail: null, // No default filtering - show all
  isTest: null, // No default filtering - show all
  isCovered: null, // No default filtering - show all
  isContained: null, // No default filtering - show all
  isMeaningful: null, // No default filtering - show all
  reactions: null,
};

/**
 * Map UI identifiers to data-model filter types.
 * Example: 'language-models' (UI) -> 'languageModelIds' (data-model)
 */
const UI_TO_DATA_MODEL_TYPE: Partial<Record<string, LogsFilterType>> = {
  'language-models': 'languageModelIds',
};

const DATA_MODEL_TO_UI_TYPE: Partial<Record<LogsFilterType, string>> = Object.entries(
  UI_TO_DATA_MODEL_TYPE
).reduce(
  (acc, [uiType, dataType]) => {
    if (dataType) {
      acc[dataType] = uiType;
    }
    return acc;
  },
  {} as Partial<Record<LogsFilterType, string>>
);

/**
 * Map filter types to the property that stores their values.
 * By default, the value key is the same as the filter type.
 * Special cases are defined here (e.g., environments -> deskIds).
 */
const VALUE_KEY_BY_TYPE: Partial<Record<LogsFilterType, keyof LogsAnalytics>> = {
  environments: 'deskIds',
};

/**
 * Centralized default values per filter type.
 * Keeps default derivation declarative and consistent.
 */
const DEFAULTS_BY_TYPE: Partial<Record<LogsFilterType, unknown>> = {
  // Array-based
  channels: [],
  tags: [],
  environments: [],
  agentIds: [],
  languageModelIds: [],
  goalAchieved: [],
  userSentiment: [],
  insightFilters: [],
  ratings: [], // No default values - user must explicitly select
  csatBrain: [], // No default values - user must explicitly select

  // Scalar
  minNumUserMessages: '',
  maxConfidence: '100',

  // Boolean/radio - using null to prevent immediate filtering
  isTest: BOOLEAN_FILTER_DEFAULTS.isTest,
  isCovered: BOOLEAN_FILTER_DEFAULTS.isCovered,
  isContained: BOOLEAN_FILTER_DEFAULTS.isContained,
  isMeaningful: BOOLEAN_FILTER_DEFAULTS.isMeaningful,
  hasCollection: BOOLEAN_FILTER_DEFAULTS.hasCollection,
  hasRecording: BOOLEAN_FILTER_DEFAULTS.hasRecording,
  hasVoicemail: BOOLEAN_FILTER_DEFAULTS.hasVoicemail,
  reactions: null,

  // String
  userName: '',
  userId: '',
  userEmail: '',
  sessionId: '',
  externalUserId: '',
  channelUserId: '',
};

/**
 * Filters considered boolean/radio-like (presence of a non-nullish value makes them active).
 * Internal to persistence logic.
 */
const BOOLEAN_FILTER_TYPES: LogsFilterType[] = [
  'hasCollection',
  'hasRecording',
  'hasVoicemail',
  'isTest',
  'isCovered',
  'isContained',
  'isMeaningful',
  'reactions',
];

/**
 * Filters considered string-based (trimmed non-empty value makes them active).
 * Internal to persistence logic.
 */
const STRING_FILTER_TYPES: LogsFilterType[] = [
  'userName',
  'userId',
  'userEmail',
  'sessionId',
  'externalUserId',
  'channelUserId',
];

/**
 * Deep copy helper to ensure all nested data is mutable.
 * @param filters Array of LogsAnalytics to clone
 * @returns Deep-cloned array
 *
 * @example
 * const mutable = createMutableCopy(reduxFilters)
 */
export function createMutableCopy(filters: LogsAnalytics[]): LogsAnalytics[] {
  if (!filters) {
    return [];
  }
  return cloneDeep(filters);
}

const sortJson = (a: unknown, b: unknown) => {
  const stringA = JSON.stringify(a);
  const stringB = JSON.stringify(b);

  if (stringA < stringB) {
    return -1;
  }
  if (stringA > stringB) {
    return 1;
  }
  return 0;
};

const normalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue).sort(sortJson);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const nextValue = (value as Record<string, unknown>)[key];
        acc[key] = normalizeValue(nextValue);
        return acc;
      }, {});
  }

  return value;
};

const normalizeFilters = (filters: LogsAnalytics[] = []) =>
  filters
    .map((filter) => normalizeValue(filter) as Record<string, unknown>)
    .sort((left, right) => {
      const leftType = typeof left.type === 'string' ? (left.type as string) : '';
      const rightType = typeof right.type === 'string' ? (right.type as string) : '';

      const typeComparison = leftType.localeCompare(rightType);
      if (typeComparison !== 0) {
        return typeComparison;
      }

      return sortJson(left, right);
    });

// A tags filter with no mode queries as 'any', so it must compare equal to an explicit 'any'.
const withTagsMode = <T extends { type?: unknown; tagsMode?: unknown }>(filter: T): T =>
  filter.type === 'tags' && !filter.tagsMode ? { ...filter, tagsMode: 'any' } : filter;

export function filtersEqual(a: LogsAnalytics[] = [], b: LogsAnalytics[] = []): boolean {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  const normalizedA = normalizeFilters(a.map(withTagsMode));
  const normalizedB = normalizeFilters(b.map(withTagsMode));

  return normalizedA.every(
    (filter, index) => JSON.stringify(filter) === JSON.stringify(normalizedB[index])
  );
}

/**
 * Produce a stable, canonical JSON string of filters to avoid URL churn.
 * Mirrors the same normalization used by filtersEqual (sorted keys and arrays).
 * @param filters Array of LogsAnalytics
 * @returns Stable JSON string
 */
export function stableStringifyFilters(filters: LogsAnalytics[] = []): string {
  return JSON.stringify(normalizeFilters(filters));
}

/**
 * Generic, type-safe comparator for arrays of filter-like objects.
 * Works with any shape that optionally carries a string `type` key.
 * Mirrors the normalization and ordering used by `filtersEqual`.
 */
export type FilterLike = { type?: string } & Record<string, unknown>;

export function filtersEqualGeneric<T extends FilterLike = FilterLike>(
  a: T[] = [],
  b: T[] = []
): boolean {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  const normalize = (items: T[]) =>
    items
      .map((item) => normalizeValue(withTagsMode(item)) as Record<string, unknown>)
      .sort((left, right) => {
        const leftType =
          typeof (left as Record<string, unknown>).type === 'string'
            ? ((left as Record<string, unknown>).type as string)
            : '';
        const rightType =
          typeof (right as Record<string, unknown>).type === 'string'
            ? ((right as Record<string, unknown>).type as string)
            : '';

        const typeComparison = leftType.localeCompare(rightType);
        if (typeComparison !== 0) {
          return typeComparison;
        }

        return sortJson(left, right);
      });

  const normalizedA = normalize(a);
  const normalizedB = normalize(b);

  return normalizedA.every(
    (filter, index) => JSON.stringify(filter) === JSON.stringify(normalizedB[index])
  );
}

/**
 * Return the canonical data-model filter type for a given UI id.
 * Falls back to the provided string when no mapping exists.
 * @param filterType UI filter identifier
 * @returns Data-model filter type
 *
 * @example
 * mapUiToDataModelType('language-models') // 'languageModelIds'
 */
export function mapUiToDataModelType(filterType: string): LogsFilterType {
  return (UI_TO_DATA_MODEL_TYPE[filterType] ?? (filterType as LogsFilterType)) as LogsFilterType;
}

/**
 * Return the UI identifier for a given data-model filter type.
 * Falls back to the original type when no mapping exists.
 * @param filterType Data-model filter type
 * @returns UI filter identifier
 */
export function mapDataModelToUiType(filterType: string): string {
  return DATA_MODEL_TO_UI_TYPE[filterType as LogsFilterType] ?? filterType;
}

/**
 * Get the property name that holds the filter values for the given type.
 * Defaults to the type itself unless a special-case is defined.
 * @param type Filter type
 * @returns Key of LogsAnalytics that stores values for this filter type
 *
 * @example
 * getValueKey('environments') // 'deskIds'
 */
export function getValueKey(type: LogsFilterType): keyof LogsAnalytics {
  return (VALUE_KEY_BY_TYPE[type] ?? (type as keyof LogsAnalytics)) as keyof LogsAnalytics;
}

/**
 * Get the default value for a given filter type.
 * @param type Filter type
 * @returns Default value (array/string/number/boolean/null)
 *
 * @example
 * getDefaultValue('ratings') // [4, 5]
 */
export function getDefaultValue(type: LogsFilterType): unknown {
  return DEFAULTS_BY_TYPE[type] ?? '';
}

/**
 * Defensive type inference: derive LogsFilterType from the properties present on a partial filter.
 * Protects against malformed filter objects (e.g., missing type) by inferring from known keys.
 * Keep precedence/order identical to legacy logic to avoid behavior changes.
 * @param filter Partial filter object from form or URL
 * @returns Inferred LogsFilterType or undefined
 */
export function inferFilterType(filter: Partial<LogsAnalytics>): LogsFilterType | undefined {
  if (!filter) {
    return undefined;
  }

  if (filter.channels) {
    return 'channels';
  }
  if ((filter as LogsAnalytics).deskIds) {
    return 'environments';
  }
  if (filter.tags) {
    return 'tags';
  }
  if (filter.languageModelIds) {
    return 'languageModelIds';
  }
  if (filter.agentIds) {
    return 'agentIds';
  }
  if (filter.ratings) {
    return 'ratings';
  }
  if (filter.csatBrain) {
    return 'csatBrain';
  }
  if (filter.goalAchieved) {
    return 'goalAchieved';
  }
  if (filter.userSentiment) {
    return 'userSentiment';
  }
  if (filter.insightFilters) {
    return 'insightFilters';
  }
  if (filter.hasCollection !== undefined) {
    return 'hasCollection';
  }
  if (filter.hasRecording !== undefined) {
    return 'hasRecording';
  }
  if (filter.hasVoicemail !== undefined) {
    return 'hasVoicemail';
  }
  if (filter.isTest !== undefined) {
    return 'isTest';
  }
  if (filter.isCovered !== undefined) {
    return 'isCovered';
  }
  if (filter.isContained !== undefined) {
    return 'isContained';
  }
  if (filter.isMeaningful !== undefined) {
    return 'isMeaningful';
  }
  if (filter.reactions !== undefined) {
    return 'reactions';
  }
  if (filter.minNumUserMessages !== undefined) {
    return 'minNumUserMessages';
  }
  if (filter.sessionId) {
    return 'sessionId';
  }
  if (filter.userId) {
    return 'userId';
  }
  if (filter.userName) {
    return 'userName';
  }
  if (filter.userEmail) {
    return 'userEmail';
  }
  if (filter.channelUserId) {
    return 'channelUserId';
  }
  if (filter.externalUserId) {
    return 'externalUserId';
  }
  if ((filter as LogsAnalytics).maxConfidence !== undefined) {
    return 'maxConfidence';
  }

  return undefined;
}

/**
 * Decide if a filter should be persisted (i.e., is meaningful and not equivalent to "all" or default).
 * Used when saving filters to Redux/URL (write path).
 * For URL hydration use sanitizeHydratedFilters to avoid over-pruning when totals are unknown.
 * @param filter Concrete filter object
 * @param context Available items and totals for channels and desks to evaluate "all-selected"
 * @returns True if filter should be persisted
 */
export function shouldPersistFilter(
  filter: LogsAnalytics,
  context: {
    channelsTotal: number;
    desksTotal: number;
    availableChannels?: string[];
    availableDesks?: string[];
  }
): boolean {
  const { availableChannels, availableDesks } = context;

  // Set-aware persistence for channels - clamp to known values
  if (filter.type === 'channels' && filter.channels) {
    const selected = filter.channels as string[];
    const avail = availableChannels ?? [];
    const availSet = new Set(avail);
    const selectedKnown = availSet.size ? selected.filter((c) => availSet.has(c)) : selected;

    return selectedKnown.length > 0;
  }

  // Set-aware persistence for environments - clamp to known values
  if (filter.type === 'environments' && (filter as LogsAnalytics).deskIds) {
    const selected = (filter as LogsAnalytics).deskIds as string[];
    const avail = availableDesks ?? [];
    const availSet = new Set(avail);
    const selectedKnown = availSet.size ? selected.filter((id) => availSet.has(id)) : selected;

    return selectedKnown.length > 0;
  }

  if (filter.type === 'tags' && filter.tags !== undefined) {
    return (filter.tags as string[]).length > 0;
  }

  if (filter.type === 'languageModelIds' && filter.languageModelIds !== undefined) {
    return (filter.languageModelIds as string[]).length > 0;
  }

  if (filter.type === 'ratings' && filter.ratings !== undefined) {
    return (filter.ratings as string[]).length > 0;
  }

  if (filter.type === 'agentIds' && filter.agentIds !== undefined) {
    return (filter.agentIds as string[]).length > 0;
  }

  if (filter.type === 'csatBrain' && filter.csatBrain !== undefined) {
    return (filter.csatBrain as number[]).length > 0;
  }

  if (filter.type === 'goalAchieved' && filter.goalAchieved !== undefined) {
    return (filter.goalAchieved as number[]).length > 0;
  }

  if (filter.type === 'userSentiment' && filter.userSentiment !== undefined) {
    return (filter.userSentiment as number[]).length > 0;
  }

  // Boolean/radio filters - persist when value is not nullish AND not empty string
  if (BOOLEAN_FILTER_TYPES.includes(filter.type)) {
    const value = (filter as unknown as Record<string, unknown>)[filter.type];
    return value !== null && value !== undefined && value !== '';
  }

  // String filters - persist when value is non-empty after trim
  if (STRING_FILTER_TYPES.includes(filter.type)) {
    const value = (filter as unknown as Record<string, string>)[filter.type];
    return Boolean(value && value.trim().length > 0);
  }

  // Number filters
  if (filter.type === 'minNumUserMessages') {
    const value = filter.minNumUserMessages as unknown as number | string;
    return typeof value === 'number' && value >= 1;
  }

  if (filter.type === 'maxConfidence') {
    const value = filter.maxConfidence as string;
    // Default is '100' (string), do not persist when at default
    return value !== undefined && value !== '100';
  }

  // Insight filters - persist when at least one configured insight carries meaningful values
  if (filter.type === 'insightFilters' && filter.insightFilters !== undefined) {
    const insightFilters = filter.insightFilters as InsightFilter[];
    return (
      insightFilters.length > 0 &&
      insightFilters.some((f) => {
        // Special case: 'exists' operator is valid with empty values
        if (f.operator === 'exists') {
          return true;
        }

        if (!f.values || f.values.length === 0) {
          return false;
        }

        // Check each value in the array for meaningful content
        return f.values.some((value) => {
          // For strings: check if non-empty after trim
          if (typeof value === 'string') {
            return value.trim().length > 0;
          }

          // For numbers: accept any number including 0, but only if explicitly set (not default)
          if (typeof value === 'number') {
            return true; // Numbers are meaningful if they exist in the array
          }

          // For booleans: accept true or false (not undefined/null)
          if (typeof value === 'boolean') {
            return true;
          }

          // For any other type, consider it meaningful if not null/undefined
          return value !== null && value !== undefined;
        });
      })
    );
  }

  // Keep any other filters by default
  return true;
}

/**
 * Sanitization for URL hydration with clamping to remove unknown/stale values.
 * First clamps arrays to available items (when known), then applies persistence rules.
 * @param filters Parsed filters from URL
 * @param context Available items and totals for channels and desks
 * @returns Sanitized and clamped filters array
 */
export function sanitizeHydratedFilters(
  filters: LogsAnalytics[],
  context: {
    channelsTotal: number;
    desksTotal: number;
    availableChannels?: string[];
    availableDesks?: string[];
  }
): LogsAnalytics[] {
  return filters
    .map((f) => {
      // Clamp channels to available set when known
      if (f.type === 'channels' && f.channels && context.availableChannels?.length) {
        const availSet = new Set(context.availableChannels);
        const clampedChannels = (f.channels as string[]).filter((c) => availSet.has(c));
        return { ...f, channels: clampedChannels };
      }

      // Clamp environments to available set when known
      if (
        f.type === 'environments' &&
        (f as LogsAnalytics).deskIds &&
        context.availableDesks?.length
      ) {
        const availSet = new Set(context.availableDesks);
        const clampedDeskIds = ((f as LogsAnalytics).deskIds as string[]).filter((id) =>
          availSet.has(id)
        );
        return { ...f, deskIds: clampedDeskIds };
      }

      return f;
    })
    .filter((filter) => {
      // Apply persistence rules to clamped filters
      if (filter.type === 'channels' || filter.type === 'environments') {
        return shouldPersistFilter(filter, context);
      }
      // Keep other filters as-is during hydration
      return true;
    });
}
